import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  Loader2,
  AlertCircle,
  ArrowLeft,
  Upload,
  CheckCircle2,
  X,
  LifeBuoy,
} from "lucide-react";
import { SUPPORT } from "@/constants/testIds";
import { useLang } from "@/lib/useLang";
import { PortalHeader, PortalFooter } from "@/components/PortalChrome";
import { authApi, portalApi, supportApi } from "@/lib/api";

const CATEGORY_ORDER = [
  "missed_pickup",
  "extra_pickup",
  "container_issue",
  "billing_question",
  "account_service_change",
  "other",
];

const RESPONSE_OPTIONS = [
  { value: "text", key: "contactChoiceText", testId: SUPPORT.radioText },
  { value: "email", key: "contactChoiceEmail", testId: SUPPORT.radioEmail },
  { value: "phone", key: "contactChoicePhone", testId: SUPPORT.radioPhone },
];

const CONFIRMATION_DESIGN = {
  submitted: "bg-[#F0E9DC] text-[#0B1930]",
  reviewing: "bg-[#F0E9DC] text-[#0B1930]",
  scheduled: "bg-[#DDF3E4] text-[#0F5132]",
  resolved: "bg-[#DDF3E4] text-[#0F5132]",
  closed: "bg-[#EDE5D4] text-[#4B5563]",
};

export default function SupportRequestNew() {
  const navigate = useNavigate();
  const { lang, t, toggle } = useLang();
  const [params] = useSearchParams();

  const [me, setMe] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);

  const [accountId, setAccountId] = useState(params.get("account") || "");
  const [locationId, setLocationId] = useState(params.get("location") || "");
  const [category, setCategory] = useState(params.get("category") || "");
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [photo, setPhoto] = useState(null);
  const [preferredResponse, setPreferredResponse] = useState("");

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [meRes, accRes] = await Promise.all([
          authApi.me(),
          portalApi.accounts(),
        ]);
        if (cancelled) return;
        if (!meRes.data.authenticated) {
          navigate("/", { replace: true });
          return;
        }
        setMe(meRes.data);
        setAccounts(accRes.data.accounts || []);
        if (!accountId && (accRes.data.accounts || []).length === 1) {
          setAccountId(accRes.data.accounts[0].account_id);
        }
        // Default preferred response to whatever channel the user is signed
        // in with — most sensible default.
        setPreferredResponse((prev) =>
          prev || (meRes.data.contact_type === "sms" ? "text" : "email"),
        );
      } catch (err) {
        if (err?.status === 401) {
          navigate("/", { replace: true });
          return;
        }
        if (!cancelled) setError(err?.detail || t.support.errorGeneric);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentAccount = useMemo(
    () => accounts.find((a) => a.account_id === accountId) || null,
    [accounts, accountId],
  );

  const locationsForAccount = currentAccount?.service_locations || [];

  useEffect(() => {
    // If the user changes accounts, reset the location.
    if (
      locationId &&
      !locationsForAccount.some((l) => l.location_id === locationId)
    ) {
      setLocationId("");
    }
  }, [accountId, locationId, locationsForAccount]);

  const onSubmit = useCallback(
    async (e) => {
      e.preventDefault();
      setError("");
      if (!accountId) {
        setError(t.support.fieldAccount);
        return;
      }
      if (!category) {
        setError(t.support.chooseCategory);
        return;
      }
      if (!subject.trim() || !message.trim() || !preferredResponse) {
        setError(t.support.errorGeneric);
        return;
      }
      setSubmitting(true);
      try {
        const form = new FormData();
        form.append("account_id", accountId);
        if (locationId) form.append("location_id", locationId);
        form.append("category", category);
        form.append("subject", subject.trim());
        form.append("message", message.trim());
        form.append("preferred_response", preferredResponse);
        if (photo) form.append("photo", photo);
        const { data } = await supportApi.create(form);
        setConfirmation(data);
      } catch (err) {
        setError(err?.detail || t.support.errorGeneric);
      } finally {
        setSubmitting(false);
      }
    },
    [
      accountId,
      category,
      locationId,
      message,
      photo,
      preferredResponse,
      subject,
      t.support,
    ],
  );

  // ------------------------------------------------------------------------
  // Confirmation view
  // ------------------------------------------------------------------------
  if (confirmation) {
    return (
      <div
        className="min-h-screen flex flex-col zmex-page-bg"
        data-testid={SUPPORT.confirmationPage}
      >
        <PortalHeader t={t} lang={lang} onToggleLang={toggle} />
        <main className="flex-1">
          <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-10 pt-14 sm:pt-20 pb-16">
            <div className="rounded-3xl bg-white border border-[#EDE5D4] shadow-[0_12px_40px_rgba(11,25,48,0.06)] p-6 sm:p-10">
              <div className="flex flex-col items-center text-center">
                <span className="flex h-16 w-16 items-center justify-center rounded-full bg-[#DDF3E4]">
                  <CheckCircle2 className="h-8 w-8 text-[#0F5132]" />
                </span>
                <h1 className="mt-5 font-heading uppercase tracking-tight text-[#0B1930] text-2xl sm:text-3xl">
                  {t.support.confirmationHeading}
                </h1>
                <p className="mt-2 text-[15px] text-[#4B5563]">
                  {t.support.confirmationSub}
                </p>
              </div>
              <dl className="mt-8 divide-y divide-[#EDE5D4] rounded-2xl border border-[#EDE5D4] bg-[#F5F1EA]/40">
                <Row
                  label={t.support.confirmationNumberLabel}
                  value={
                    <span
                      className="font-mono font-semibold text-[#0B1930]"
                      data-testid={SUPPORT.confirmationNumber}
                    >
                      {confirmation.confirmation_number}
                    </span>
                  }
                />
                <Row
                  label={t.support.confirmationCategoryLabel}
                  value={t.support.categoryLabels[confirmation.category]}
                />
                {confirmation.location_label && (
                  <Row
                    label={t.support.confirmationLocationLabel}
                    value={confirmation.location_label}
                  />
                )}
                <Row
                  label={t.support.confirmationSubmittedLabel}
                  value={new Date(confirmation.created_at).toLocaleString(
                    lang === "es" ? "es-US" : "en-US",
                  )}
                />
                <Row
                  label={t.support.confirmationStatusLabel}
                  value={
                    <span
                      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-semibold ${
                        CONFIRMATION_DESIGN[confirmation.status] ||
                        CONFIRMATION_DESIGN.submitted
                      }`}
                    >
                      {t.support.statusValues[confirmation.status] ||
                        confirmation.status}
                    </span>
                  }
                />
              </dl>
              <div className="mt-6 rounded-xl border border-dashed border-[#C8A46B]/60 bg-[#F0E9DC]/40 px-4 py-3 text-sm text-[#4B5563]">
                <p className="font-semibold text-[#0B1930]">
                  {t.support.confirmationNextStep}
                </p>
                <p className="mt-1">{t.support.confirmationNextStepBody}</p>
              </div>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <button
                  type="button"
                  onClick={() => navigate("/portal/dashboard")}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full border border-[#EDE5D4] bg-white px-5 py-3 text-sm font-semibold text-[#0B1930] hover:bg-[#F5F1EA]"
                  data-testid={SUPPORT.confirmationBackDashboardBtn}
                >
                  <ArrowLeft className="h-4 w-4" />
                  {t.support.backToDashboard}
                </button>
                <button
                  type="button"
                  onClick={() => navigate("/portal/support/requests")}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-full bg-[#0B1930] hover:bg-[#162B4D] text-white px-5 py-3 text-sm font-semibold"
                  data-testid={SUPPORT.confirmationViewRequestsBtn}
                >
                  {t.support.viewMyRequests}
                </button>
              </div>
            </div>
          </div>
        </main>
        <PortalFooter t={t} />
      </div>
    );
  }

  // ------------------------------------------------------------------------
  // Form view
  // ------------------------------------------------------------------------
  return (
    <div
      className="min-h-screen flex flex-col zmex-page-bg"
      data-testid={SUPPORT.newPage}
    >
      <PortalHeader t={t} lang={lang} onToggleLang={toggle} />
      <main className="flex-1">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-10 pt-10 sm:pt-14 pb-16">
          <button
            type="button"
            onClick={() => navigate("/portal/dashboard")}
            className="inline-flex items-center gap-1.5 text-sm text-[#4B5563] hover:text-[#0B1930] transition-colors"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            {t.support.backToDashboard}
          </button>

          <div className="mt-4 flex items-center gap-3">
            <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#F0E9DC]">
              <LifeBuoy className="h-5 w-5 text-[#0B1930]" />
            </span>
            <div>
              <h1 className="font-heading uppercase tracking-tight text-[#0B1930] text-2xl sm:text-3xl">
                {t.support.newHeading}
              </h1>
              <p className="mt-1 text-sm text-[#4B5563]">{t.support.newSub}</p>
            </div>
          </div>

          {loading ? (
            <div className="mt-10 flex items-center gap-3 text-[#4B5563]">
              <Loader2 className="h-5 w-5 animate-spin" />
            </div>
          ) : (
            <form
              onSubmit={onSubmit}
              className="mt-8 space-y-5 rounded-3xl bg-white border border-[#EDE5D4] p-6 sm:p-8"
              data-testid={SUPPORT.newForm}
              encType="multipart/form-data"
              noValidate
            >
              <Field label={t.support.fieldAccount}>
                <select
                  value={accountId}
                  onChange={(e) => setAccountId(e.target.value)}
                  className="w-full rounded-xl border border-[#E6E0D1] bg-white px-3 py-3 text-[15px] text-[#0B1930] outline-none focus:border-[#C8A46B] focus:ring-2 focus:ring-[#C8A46B]/25"
                  required
                  data-testid={SUPPORT.selectAccount}
                >
                  <option value="" disabled>
                    —
                  </option>
                  {accounts.map((a) => (
                    <option key={a.account_id} value={a.account_id}>
                      {a.display_name}
                    </option>
                  ))}
                </select>
              </Field>

              {locationsForAccount.length > 0 && (
                <Field label={t.support.fieldLocation}>
                  <select
                    value={locationId}
                    onChange={(e) => setLocationId(e.target.value)}
                    className="w-full rounded-xl border border-[#E6E0D1] bg-white px-3 py-3 text-[15px] text-[#0B1930] outline-none focus:border-[#C8A46B] focus:ring-2 focus:ring-[#C8A46B]/25"
                    data-testid={SUPPORT.selectLocation}
                  >
                    <option value="">{t.support.chooseLocation}</option>
                    {locationsForAccount.map((l) => (
                      <option key={l.location_id} value={l.location_id}>
                        {l.label}
                      </option>
                    ))}
                  </select>
                </Field>
              )}

              <Field label={t.support.fieldCategory}>
                <select
                  value={category}
                  onChange={(e) => setCategory(e.target.value)}
                  className="w-full rounded-xl border border-[#E6E0D1] bg-white px-3 py-3 text-[15px] text-[#0B1930] outline-none focus:border-[#C8A46B] focus:ring-2 focus:ring-[#C8A46B]/25"
                  required
                  data-testid={SUPPORT.selectCategory}
                >
                  <option value="" disabled>
                    {t.support.chooseCategory}
                  </option>
                  {CATEGORY_ORDER.map((c) => (
                    <option key={c} value={c}>
                      {t.support.categoryLabels[c]}
                    </option>
                  ))}
                </select>
              </Field>

              <Field label={t.support.fieldSubject}>
                <input
                  type="text"
                  maxLength={120}
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  placeholder={t.support.fieldSubjectPlaceholder}
                  className="w-full rounded-xl border border-[#E6E0D1] bg-white px-3 py-3 text-[15px] text-[#0B1930] outline-none focus:border-[#C8A46B] focus:ring-2 focus:ring-[#C8A46B]/25"
                  required
                  data-testid={SUPPORT.inputSubject}
                />
              </Field>

              <Field label={t.support.fieldMessage}>
                <textarea
                  rows={5}
                  maxLength={4000}
                  value={message}
                  onChange={(e) => setMessage(e.target.value)}
                  placeholder={t.support.fieldMessagePlaceholder}
                  className="w-full rounded-xl border border-[#E6E0D1] bg-white px-3 py-3 text-[15px] text-[#0B1930] outline-none focus:border-[#C8A46B] focus:ring-2 focus:ring-[#C8A46B]/25"
                  required
                  data-testid={SUPPORT.inputMessage}
                />
              </Field>

              <Field label={t.support.fieldPhoto}>
                <PhotoInput
                  value={photo}
                  onChange={setPhoto}
                  hint={t.support.fieldPhotoHint}
                />
              </Field>

              <div>
                <label className="text-sm font-medium text-[#0B1930]">
                  {t.support.fieldContact}
                </label>
                {me && (
                  <p className="mt-1 text-xs text-[#4B5563]">
                    {t.support.contactMasked}{" "}
                    <span className="font-semibold text-[#0B1930]">
                      {me.contact_display_masked}
                    </span>
                  </p>
                )}
                <div className="mt-3 flex flex-wrap gap-2">
                  {RESPONSE_OPTIONS.map((opt) => (
                    <button
                      type="button"
                      key={opt.value}
                      onClick={() => setPreferredResponse(opt.value)}
                      className={`px-4 py-2 rounded-full text-sm font-semibold border transition-colors ${
                        preferredResponse === opt.value
                          ? "bg-[#0B1930] text-white border-[#0B1930]"
                          : "bg-white text-[#0B1930] border-[#EDE5D4] hover:bg-[#F5F1EA]"
                      }`}
                      data-testid={opt.testId}
                    >
                      {t.support[opt.key]}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div
                  className="flex items-start gap-2 rounded-xl bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700"
                  role="alert"
                  data-testid={SUPPORT.errorMsg}
                >
                  <AlertCircle className="h-4 w-4 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={submitting}
                className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#C8A46B] hover:bg-[#B8925A] text-[#0B1930] px-6 py-4 text-sm font-bold uppercase tracking-wide transition-colors disabled:opacity-70"
                data-testid={SUPPORT.submitBtn}
              >
                {submitting ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t.support.submitting}
                  </>
                ) : (
                  t.support.submit
                )}
              </button>
            </form>
          )}
        </div>
      </main>
      <PortalFooter t={t} />
    </div>
  );
}

function Field({ label, children }) {
  return (
    <label className="block">
      <span className="text-sm font-medium text-[#0B1930]">{label}</span>
      <div className="mt-1">{children}</div>
    </label>
  );
}

function Row({ label, value }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-3">
      <span className="text-xs uppercase tracking-wide text-[#4B5563]">
        {label}
      </span>
      <span className="text-sm text-[#0B1930] text-right">{value}</span>
    </div>
  );
}

function PhotoInput({ value, onChange, hint }) {
  return (
    <div>
      {value ? (
        <div className="flex items-center gap-3 rounded-xl border border-[#E6E0D1] bg-[#F5F1EA] px-4 py-3">
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white">
            <Upload className="h-4 w-4 text-[#0B1930]" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-[#0B1930] truncate">
              {value.name}
            </p>
            <p className="text-xs text-[#4B5563]">
              {(value.size / 1024).toFixed(1)} KB
            </p>
          </div>
          <button
            type="button"
            onClick={() => onChange(null)}
            className="text-[#4B5563] hover:text-[#0B1930]"
            aria-label="Remove photo"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      ) : (
        <label
          className="flex items-center gap-3 rounded-xl border border-dashed border-[#E6E0D1] bg-white px-4 py-3 cursor-pointer hover:bg-[#F5F1EA] transition-colors"
        >
          <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#F0E9DC]">
            <Upload className="h-4 w-4 text-[#0B1930]" />
          </span>
          <div className="flex-1">
            <p className="text-sm font-semibold text-[#0B1930]">
              Choose a photo
            </p>
            <p className="text-xs text-[#4B5563]">{hint}</p>
          </div>
          <input
            type="file"
            accept="image/jpeg,image/png,image/webp,image/heic,image/heif"
            className="hidden"
            data-testid={SUPPORT.inputPhoto}
            onChange={(e) => onChange(e.target.files?.[0] || null)}
          />
        </label>
      )}
    </div>
  );
}
